import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
  type QuerySnapshot,
  type Timestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import { db } from '@/src/lib/firebase';
import { functions } from '@/src/lib/functions';
import { isReaction } from '@/src/features/posts/post-service';
import type { Reaction } from '@/src/features/posts/post-service';

export type NotificationType =
  | 'post_like'
  | 'post_reaction'
  | 'post_comment'
  | 'post_mention'
  | 'profile_follow'
  | 'invite_acceptance'
  | 'comment_reaction'
  | 'comment_mention'
  | 'vehicle_reaction'
  | 'vehicle_comment'
  | 'vehicle_comment_reaction'
  | 'vehicle_mention'
  | 'vehicle_comment_mention'
  | 'platform_update_digest';
export type NotificationReason = 'post_owner' | 'comment_thread' | null;

export type AppNotification = {
  id: string;
  recipientUid: string;
  actorUid: string;
  actorFullName: string | null;
  actorPhotoURL: string | null;
  type: NotificationType;
  reaction: Reaction | null;
  postId: string | null;
  commentId: string | null;
  url: string | null;
  vehicleId: string | null;
  vehiclePreview: string | null;
  reason: NotificationReason;
  postPreview: string | null;
  commentPreview: string | null;
  createdAt: Timestamp | null;
  readAt: Timestamp | null;
};

const refreshUnreadNotificationCountCallable = httpsCallable<
  Record<string, never>,
  { count: number; ok: true }
>(functions, 'refreshUnreadNotificationCount');

function parseNotificationsSnapshot(
  snapshot: QuerySnapshot,
): AppNotification[] {
  return snapshot.docs.map((notificationSnapshot) => {
    const data = notificationSnapshot.data();

    return {
      id: notificationSnapshot.id,
      recipientUid: data.recipientUid,
      actorUid: data.actorUid,
      actorFullName: data.actorFullName ?? data.actorDisplayName ?? null,
      actorPhotoURL: data.actorPhotoURL ?? null,
      type: data.type,
      reaction: isReaction(data.reaction) ? data.reaction : null,
      postId: data.postId ?? null,
      commentId: data.commentId ?? null,
      url: typeof data.url === 'string' ? data.url : null,
      vehicleId: typeof data.vehicleId === 'string' ? data.vehicleId : null,
      vehiclePreview:
        typeof data.vehiclePreview === 'string' ? data.vehiclePreview : null,
      reason:
        data.reason === 'comment_thread' || data.reason === 'post_owner'
          ? data.reason
          : null,
      postPreview: data.postPreview ?? null,
      commentPreview: data.commentPreview ?? null,
      createdAt: data.createdAt ?? null,
      readAt: data.readAt ?? null,
    };
  });
}

export function subscribeToNotifications({
  limitCount = 50,
  onError,
  onNext,
  uid,
}: {
  limitCount?: number;
  onError: (error: Error) => void;
  onNext: (notifications: AppNotification[]) => void;
  uid: string;
}) {
  const notificationsQuery = query(
    collection(db, 'users', uid, 'notifications'),
    orderBy('createdAt', 'desc'),
    limit(limitCount),
  );

  return onSnapshot(
    notificationsQuery,
    (snapshot) => {
      onNext(parseNotificationsSnapshot(snapshot));
    },
    onError,
  );
}

export function subscribeToUnreadNotificationCount({
  onError,
  onNext,
  uid,
}: {
  onError: (error: Error) => void;
  onNext: (count: number) => void;
  uid: string;
}) {
  const userRef = doc(db, 'users', uid);
  let syncRequested = false;

  return onSnapshot(
    userRef,
    (snapshot) => {
      const data = snapshot.data();
      const count =
        typeof data?.unreadNotificationCount === 'number'
          ? data.unreadNotificationCount
          : typeof data?.notificationCounters?.unread === 'number'
            ? data.notificationCounters.unread
            : null;

      if (count != null) {
        onNext(Math.max(0, Math.floor(count)));
        return;
      }

      onNext(0);

      if (!syncRequested) {
        syncRequested = true;
        void refreshUnreadNotificationCountCallable({}).catch((error) => {
          onError(
            error instanceof Error
              ? error
              : new Error('Unable to refresh notification count.'),
          );
        });
      }
    },
    onError,
  );
}

export async function markNotificationRead({
  notificationId,
  uid,
}: {
  notificationId: string;
  uid: string;
}) {
  const notificationRef = doc(
    db,
    'users',
    uid,
    'notifications',
    notificationId,
  );
  const notificationSnapshot = await getDoc(notificationRef);

  if (
    !notificationSnapshot.exists() ||
    notificationSnapshot.data().readAt != null
  ) {
    return;
  }

  await updateDoc(notificationRef, {
    readAt: serverTimestamp(),
  });
}

export async function markAllNotificationsRead(uid: string) {
  const notificationsRef = collection(db, 'users', uid, 'notifications');

  while (true) {
    const unreadSnapshot = await getDocs(
      query(notificationsRef, where('readAt', '==', null), limit(450)),
    );

    if (unreadSnapshot.empty) {
      return;
    }

    const batch = writeBatch(db);
    unreadSnapshot.docs.forEach((notificationSnapshot) => {
      batch.update(notificationSnapshot.ref, {
        readAt: serverTimestamp(),
      });
    });
    await batch.commit();

    if (unreadSnapshot.size < 450) {
      return;
    }
  }
}
